import { auditLogger, isValidUUID } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

import { getCurrentUserId, verifyOwnership, withSecurity } from './secureServiceWrapper';

export interface ContractReviewRequest {
  jurisdiction: string;
  document_id: string;
}

export interface KeyTerms {
  parties: string[];
  term: string;
  payment_structure: string;
  termination_rights: string;
  liability_structure: string;
}

export interface ContractOverview {
  contract_type: string;
  summary: string;
  key_terms: KeyTerms;
}

export interface ClausePresent {
  clause_name: string;
  category: string;
  section_reference: string;
  excerpt: string;
}

export interface MissingClause {
  clause_name: string;
  why_expected: string;
  risk_of_absence: string;
  recommendation: string;
}

export interface ConflictingClause {
  issue_description: string;
  section_references: string[];
  conflicting_text: string[];
  risk_analysis: string;
  recommendation: string;
}

export interface RedFlag {
  issue: string;
  section_reference: string;
  risk_level: 'Low' | 'Medium' | 'High';
  explanation: string;
  recommendation: string;
}

export interface MarketDeviation {
  provision: string;
  section_reference: string;
  standard_market_position: string;
  observed_deviation: string;
  implication: string;
  recommendation: string;
}

export interface DraftingQuality {
  clarity_assessment: string;
  structural_assessment: string;
  inconsistencies: string[];
  enforceability_concerns: string[];
}

export interface ComplianceWarning {
  issue: string;
  section_reference: string;
  jurisdictional_requirement: string;
  explanation: string;
  recommendation: string;
}

export interface FavorabilityAssessment {
  clause_name: string;
  section_reference: string;
  favorability: 'Favorable' | 'Neutral' | 'Unfavorable';
  explanation: string;
}

export interface OverallAssessment {
  risk_level: 'Low' | 'Medium' | 'High';
  risk_score: number;
  summary_explanation: string;
}

export interface ContractReviewResult {
  contract_overview: ContractOverview;
  clauses_present: ClausePresent[];
  missing_clauses: MissingClause[];
  conflicting_clauses: ConflictingClause[];
  red_flags: RedFlag[];
  market_deviations: MarketDeviation[];
  drafting_quality: DraftingQuality;
  compliance_warnings: ComplianceWarning[];
  favorability_assessments: FavorabilityAssessment[];
  overall_assessment: OverallAssessment;
  completed_at?: string;
}

interface LegacyContractReviewResult {
  high_level_summary?: {
    title?: { text?: string };
    parties?: string[];
    effective_date?: string | null;
    term?: string | null;
    renewal?: string | null;
    termination?: string | null;
    governing_law?: string | null;
    key_financial_terms?: string | null;
    other_metadata?: Record<string, unknown>;
  };
  missing_clauses?: Array<{ clause_name: string; importance?: string; explanation?: string }>;
  conflicting_clauses?: Array<{ conflict_id?: string; sections_in_conflict?: string[]; description?: string }>;
  anomalies?: Array<{ clause: string; type: string; explanation: string }>;
  risk_flags?: Array<{ clause: string; risk_level: string; explanation: string }>;
  deviations_from_standard?: Array<{ clause: string; deviation_type: string; explanation: string }>;
  compliance_warnings?: Array<{ clause: string; jurisdictional_issue: string; explanation: string }>;
  favorability_assessments?: Array<{ clause: string; favorability: string; reason?: string }>;
  completed_at?: string;
}

export interface ContractReviewDatabaseResult {
  id: string;
  document_id: string;
  status: string;
  request: ContractReviewRequest & { contract_id?: string; entity_id?: string; client_id?: string };
  result?: ContractReviewResult | LegacyContractReviewResult;
  created_at: string;
  completed_at?: string;
  pipeline_version?: string;
}

function isNewFormat(r: unknown): r is ContractReviewResult {
  return r != null && typeof r === 'object' && 'contract_overview' in r;
}

export function normalizeContractReviewResult(
  raw: ContractReviewResult | LegacyContractReviewResult | undefined
): ContractReviewResult | undefined {
  if (!raw) return undefined;
  if (isNewFormat(raw)) return raw;
  const leg = raw as LegacyContractReviewResult;
  const summary = leg.high_level_summary;
  const contract_overview: ContractOverview = {
    contract_type: summary?.title?.text ?? 'Contract',
    summary:
      [
        summary?.parties?.length ? `Parties: ${summary.parties.join(', ')}` : '',
        summary?.term ? `Term: ${summary.term}` : '',
        summary?.termination ? `Termination: ${summary.termination}` : '',
      ]
        .filter(Boolean)
        .join('. ') || 'No summary available',
    key_terms: {
      parties: summary?.parties ?? [],
      term: summary?.term ?? '',
      payment_structure: summary?.key_financial_terms ?? '',
      termination_rights: summary?.termination ?? '',
      liability_structure: '',
    },
  };
  const missing_clauses: MissingClause[] = (leg.missing_clauses ?? []).map((m) => ({
    clause_name: m.clause_name,
    why_expected: '',
    risk_of_absence: m.explanation ?? '',
    recommendation: '',
  }));
  const conflicting_clauses: ConflictingClause[] = (leg.conflicting_clauses ?? []).map((c) => ({
    issue_description: c.description ?? '',
    section_references: c.sections_in_conflict ?? [],
    conflicting_text: [],
    risk_analysis: '',
    recommendation: '',
  }));
  const red_flags: RedFlag[] = [
    ...(leg.risk_flags ?? []).map((r) => ({
      issue: r.clause,
      section_reference: '',
      risk_level: r.risk_level as 'Low' | 'Medium' | 'High',
      explanation: r.explanation,
      recommendation: '',
    })),
    ...(leg.anomalies ?? []).map((a) => ({
      issue: a.clause,
      section_reference: '',
      risk_level: 'Medium' as const,
      explanation: a.explanation,
      recommendation: '',
    })),
  ];
  const market_deviations: MarketDeviation[] = (leg.deviations_from_standard ?? []).map((d) => ({
    provision: d.clause,
    section_reference: '',
    standard_market_position: '',
    observed_deviation: d.explanation,
    implication: '',
    recommendation: '',
  }));
  const compliance_warnings: ComplianceWarning[] = (leg.compliance_warnings ?? []).map((w) => ({
    issue: w.clause,
    section_reference: '',
    jurisdictional_requirement: w.jurisdictional_issue,
    explanation: w.explanation,
    recommendation: '',
  }));
  const favorability_assessments: FavorabilityAssessment[] = (leg.favorability_assessments ?? []).map((a) => ({
    clause_name: a.clause,
    section_reference: '',
    favorability: a.favorability as 'Favorable' | 'Neutral' | 'Unfavorable',
    explanation: a.reason ?? '',
  }));
  return {
    contract_overview,
    clauses_present: [],
    missing_clauses,
    conflicting_clauses,
    red_flags,
    market_deviations,
    drafting_quality: {
      clarity_assessment: '',
      structural_assessment: '',
      inconsistencies: [],
      enforceability_concerns: [],
    },
    compliance_warnings,
    favorability_assessments,
    overall_assessment: {
      risk_level: red_flags.some((r) => r.risk_level === 'High')
        ? 'High'
        : red_flags.every((r) => r.risk_level === 'Low')
          ? 'Low'
          : 'Medium',
      risk_score: 5,
      summary_explanation: 'Contract analysis completed.',
    },
    completed_at: leg.completed_at,
  };
}

export function getContractReviewDisplayName(
  raw: ContractReviewResult | LegacyContractReviewResult | undefined
): string {
  const result = normalizeContractReviewResult(raw);
  if (!result) return 'Contract Review';
  const overview = result.contract_overview;
  if (overview?.contract_type) return overview.contract_type;
  if (overview?.summary) {
    const truncated = overview.summary.slice(0, 60);
    return truncated.length < overview.summary.length ? `${truncated}…` : truncated;
  }
  return 'Contract Review';
}

export const contractService = {
  getUserContractReviews: async (): Promise<ContractReviewDatabaseResult[]> => {
    return withSecurity(
      async () => {
        const supabase = await getSupabase();

        const { data: vaultDocs } = await supabase.from('client_vault_documents').select('id');

        const docIds = (vaultDocs || []).map((d: { id: string }) => d.id);
        if (docIds.length === 0) return [];

        const { data, error } = await supabase
          .from('contract_review_results')
          .select('*')
          .in('document_id', docIds)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      { requireAuth: true, rateLimitKey: 'contract_get_user' }
    );
  },

  getClientContractReviews: async (clientId: string): Promise<ContractReviewDatabaseResult[]> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'contract' });
          return [];
        }

        const supabase = await getSupabase();

        const { data: vaultDocs } = await supabase
          .from('client_vault_documents')
          .select('id')
          .eq('client_id', clientId);

        const docIds = (vaultDocs || []).map((d: { id: string }) => d.id);

        if (docIds.length === 0) return [];

        const { data, error } = await supabase
          .from('contract_review_results')
          .select('*')
          .in('document_id', docIds)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      { requireAuth: true, rateLimitKey: 'contract_get_client' }
    );
  },

  getCurrentUserContractReviews: async (): Promise<ContractReviewDatabaseResult[]> => {
    return withSecurity(
      async () => {
        const supabase = await getSupabase();

        const { data: vaultDocs } = await supabase.from('client_vault_documents').select('id');

        const docIds = (vaultDocs || []).map((d: { id: string }) => d.id);
        if (docIds.length === 0) return [];

        const { data, error } = await supabase
          .from('contract_review_results')
          .select('*')
          .in('document_id', docIds)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
      },
      { requireAuth: true, rateLimitKey: 'contract_get_current' }
    );
  },

  getContractReviewByDocumentId: async (documentId: string): Promise<ContractReviewDatabaseResult | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(documentId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_document_id', service: 'contract' });
          return null;
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('contract_review_results')
          .select('*')
          .eq('document_id', documentId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        return data ?? null;
      },
      { requireAuth: true, rateLimitKey: 'contract_get_by_doc' }
    );
  },

  getContractReview: async (resultId: string): Promise<ContractReviewDatabaseResult | null> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'contract' });
          return null;
        }

        const isOwner = await verifyOwnership('contract_review_results', resultId);
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { resourceId: resultId, service: 'contract' });
          throw new Error('Access denied');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('contract_review_results')
          .select('*')
          .eq('id', resultId)
          .maybeSingle();

        if (error) throw error;
        return data;
      },
      { requireAuth: true, rateLimitKey: 'contract_get_review' }
    );
  },

  subscribeToContractUpdates: async (callback: (payload: unknown) => void) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId) {
      auditLogger.log('AUTH_FAILURE', { reason: 'no_user', service: 'contract_subscribe' });
      return null;
    }

    const supabase = await getSupabase();
    const channel = supabase.channel(`contract_review_results_changes_${currentUserId}_${crypto.randomUUID()}`);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'contract_review_results',
      },
      callback
    );

    await channel.subscribe();
    return channel;
  },

  subscribeToContractUpdatesForDocument: async (documentId: string, callback: (payload: unknown) => void) => {
    if (!isValidUUID(documentId)) {
      auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_document_id', service: 'contract_subscribe' });
      return null;
    }

    const currentUserId = await getCurrentUserId();
    if (!currentUserId) {
      auditLogger.log('AUTH_FAILURE', { reason: 'no_user', service: 'contract_subscribe' });
      return null;
    }

    const supabase = await getSupabase();
    const channel = supabase.channel(`contract_review_results_doc_${documentId}_${crypto.randomUUID()}`);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'contract_review_results',
        filter: `document_id=eq.${documentId}`,
      },
      callback
    );

    await channel.subscribe();
    return channel;
  },

  deleteContractReview: async (resultId: string): Promise<void> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(resultId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_result_id', service: 'contract' });
          throw new Error('Invalid result ID');
        }

        const isOwner = await verifyOwnership('contract_review_results', resultId, 'entity_id');
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { resourceId: resultId, service: 'contract_delete' });
          throw new Error('Access denied');
        }

        const supabase = await getSupabase();

        const { data: result, error: fetchError } = await supabase
          .from('contract_review_results')
          .select('status')
          .eq('id', resultId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!result) {
          throw new Error('Contract review not found');
        }

        if (result.status !== 'COMPLETED' && result.status !== 'FAILED') {
          throw new Error('Cannot delete contract review: The review must be COMPLETED or FAILED');
        }

        const { error: deleteError } = await supabase.from('contract_review_results').delete().eq('id', resultId);

        if (deleteError) {
          throw deleteError;
        }
      },
      { requireAuth: true, rateLimitKey: 'contract_delete', rateLimitMax: 20 }
    );
  },
};
